import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import SaveResultsWizard from '../../components/chemistry/SaveResultsWizard';
import StructureImage from '../../components/chemistry/StructureImage';
import { useWorkbenchContext } from '../../hooks/useWorkbenchContext';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';

const WorkbenchADMET: React.FC = () => {
  const { smiles: ctxSmiles, moleculeId: ctxMoleculeId, fileId: ctxFileId, projectId: ctxProjectId } = useWorkbenchContext();
  const [smiles, setSmiles] = useState(ctxSmiles || '');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [clearance, setClearance] = useState<any>(null);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const { bailOutText, setHasSaved } = useToolStep();

  const runPrediction = async () => {
    if (!smiles.trim()) return;
    setIsRunning(true);
    setError('');
    setResult(null);
    setClearance(null);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const [admetResp, clResp] = await Promise.all([
        apiClient.post('/v2/admet/predict', { smiles }, { signal: controller.signal }),
        apiClient.post('/v2/admet/intrinsic-clearance', { smiles }, { signal: controller.signal }).catch(() => null),
      ]);
      setResult(admetResp.data);
      if (clResp) setClearance(clResp.data);
    } catch (err: any) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        setError(err.response?.data?.detail || err.message || 'Prediction failed');
      }
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  const colorFor = (ok: boolean) => ok ? '#16a34a' : '#dc2626';

  return (
    <>
      <WorkbenchContextBar bailOutText={bailOutText} />
      <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={statusBarStyle}>
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="admet" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: RDKit</span>
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={() => abortRef.current?.abort()} isRunning={isRunning} />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={h2Style}>ADMET Prediction</h2>
            <p style={descStyle}>Enter a molecule to predict absorption, distribution, metabolism, excretion, and toxicity properties.</p>

            {/* Input */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button onClick={() => setSmiles('CC(=O)Oc1ccccc1C(=O)O')} style={btnStyle}>Aspirin</button>
              <button onClick={() => setSmiles('CC(C)Cc1ccc(cc1)C(C)C(=O)O')} style={btnStyle}>Ibuprofen</button>
              <button onClick={() => setSmiles('c1ccc2c(c1)cc1ccc3cccc4ccc2c1c34')} style={btnStyle}>Triphenylene</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input type="text" value={smiles} onChange={(e) => setSmiles(e.target.value)}
                placeholder="Enter molecule notation..."
                onKeyDown={(e) => { if (e.key === 'Enter') runPrediction(); }}
                style={{ ...textareaStyle, height: 40, flex: 1 }} />
              <button onClick={runPrediction} disabled={!smiles.trim() || isRunning}
                style={{ ...primaryBtnStyle, opacity: smiles.trim() && !isRunning ? 1 : 0.5 }}>
                {isRunning ? 'Running...' : 'Predict'}
              </button>
            </div>

            {isRunning && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={spinnerStyle} /><span style={{ color: '#f59e0b' }}>Computing ADMET properties...</span>
              </div>
            )}
            {error && <div style={errorStyle}>{error}</div>}

            {/* Results */}
            {result && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Verdict */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>Drug-like</div>
                    <div style={{ ...resultValueStyle, color: colorFor(result.druglike) }}>{result.druglike ? 'Yes' : 'No'}</div>
                  </div>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>GI Absorption</div>
                    <div style={{ ...resultValueStyle, color: result.gi_absorption === 'High' ? '#16a34a' : '#d97706' }}>{result.gi_absorption}</div>
                  </div>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>BBB Permeant</div>
                    <div style={{ ...resultValueStyle, fontSize: 16 }}>{result.bbb_permeant}</div>
                  </div>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>Solubility</div>
                    <div style={{ ...resultValueStyle, fontSize: 14 }}>{result.esol_class}</div>
                  </div>
                </div>

                {/* Lipinski / Veber */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>Lipinski</div>
                    <div style={{ ...resultValueStyle, color: result.lipinski_violations === 0 ? '#16a34a' : '#dc2626' }}>
                      {result.lipinski_violations === 0 ? 'Pass' : `${result.lipinski_violations} violations`}
                    </div>
                  </div>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>Veber</div>
                    <div style={{ ...resultValueStyle, color: result.veber_violations === 0 ? '#16a34a' : '#dc2626' }}>
                      {result.veber_violations === 0 ? 'Pass' : `${result.veber_violations} violations`}
                    </div>
                  </div>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>Bioavailability</div>
                    <div style={resultValueStyle}>{result.bioavailability_score}</div>
                  </div>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>Lead-like</div>
                    <div style={{ ...resultValueStyle, fontSize: 16 }}>{result.leadlikeness_class}</div>
                  </div>
                </div>

                {/* Safety */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>PAINS Alerts</div>
                    <div style={{ ...resultValueStyle, color: result.pains_alerts === 0 ? '#16a34a' : '#dc2626' }}>{result.pains_alerts}</div>
                  </div>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>Brenk Alerts</div>
                    <div style={{ ...resultValueStyle, color: result.brenk_alerts === 0 ? '#16a34a' : '#d97706' }}>{result.brenk_alerts}</div>
                  </div>
                </div>
                {result.pains_descriptions?.length > 0 && (
                  <div style={{ color: '#dc2626', fontSize: 13, fontFamily: 'monospace' }}>PAINS: {result.pains_descriptions.join(', ')}</div>
                )}
                {result.brenk_descriptions?.length > 0 && (
                  <div style={{ color: '#d97706', fontSize: 13, fontFamily: 'monospace' }}>Brenk: {result.brenk_descriptions.join(', ')}</div>
                )}

                {/* Clearance */}
                {clearance && (
                  <>
                    <h3 style={{ color: '#a7abb6', fontSize: 14, fontWeight: 600, margin: '8px 0 0' }}>Intrinsic Clearance</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                      <div style={resultCardStyle}>
                        <div style={resultLabelStyle}>CLint</div>
                        <div style={resultValueStyle}>{clearance.clint}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{'\u00B5'}L/min/mg</div>
                      </div>
                      <div style={resultCardStyle}>
                        <div style={resultLabelStyle}>Hepatic CL</div>
                        <div style={resultValueStyle}>{clearance.hepatic_clearance}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>mL/min/kg</div>
                      </div>
                      <div style={resultCardStyle}>
                        <div style={resultLabelStyle}>Extraction Ratio</div>
                        <div style={{ ...resultValueStyle, color: clearance.extraction_ratio < 0.3 ? '#16a34a' : clearance.extraction_ratio < 0.7 ? '#d97706' : '#dc2626' }}>
                          {clearance.extraction_ratio}
                        </div>
                      </div>
                      <div style={resultCardStyle}>
                        <div style={resultLabelStyle}>Classification</div>
                        <div style={{ ...resultValueStyle, fontSize: 16, color: clearance.classification === 'Low' ? '#16a34a' : clearance.classification === 'Moderate' ? '#d97706' : '#dc2626' }}>
                          {clearance.classification}
                        </div>
                      </div>
                      <div style={resultCardStyle}>
                        <div style={resultLabelStyle}>fu (unbound)</div>
                        <div style={resultValueStyle}>{clearance.fu}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>
                      {clearance.method}
                    </div>
                  </>
                )}

                {/* Physicochemical */}
                <h3 style={{ color: '#a7abb6', fontSize: 14, fontWeight: 600, margin: '8px 0 0' }}>Physicochemical Properties</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                  {[
                    { k: 'mw', l: 'MW', s: ' Da' }, { k: 'logp', l: 'LogP', s: '' }, { k: 'tpsa', l: 'TPSA', s: ' A\u00B2' },
                    { k: 'hbd', l: 'HBD', s: '' }, { k: 'hba', l: 'HBA', s: '' }, { k: 'rotatable_bonds', l: 'RotBonds', s: '' },
                    { k: 'aromatic_rings', l: 'Arom Rings', s: '' }, { k: 'ring_count', l: 'Rings', s: '' },
                    { k: 'fsp3', l: 'Fsp3', s: '' }, { k: 'molar_refractivity', l: 'MR', s: '' },
                    { k: 'esol_logs', l: 'LogS', s: '' },
                  ].map(({ k, l, s }) => (
                    <div key={k} style={{ ...resultCardStyle, padding: '8px 10px' }}>
                      <div style={{ ...resultLabelStyle, fontSize: 10 }}>{l}</div>
                      <div style={{ color: '#f59e0b', fontSize: 14, fontWeight: 600, fontFamily: 'monospace' }}>{result[k]}{s}</div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  <button onClick={() => navigator.clipboard.writeText(JSON.stringify(result, null, 2))} style={btnStyle}>Copy JSON</button>
                  <button onClick={() => {
                    const lines = ['parameter,value', ...Object.entries(result).map(([k, v]) => `${k},"${v}"`)];
                    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'admet_results.csv'; a.click(); URL.revokeObjectURL(url);
                  }} style={primaryBtnStyle}>Download CSV</button>
                  <button onClick={() => setSaveOpen(true)} style={btnStyle}>Save to Project</button>
                </div>
                <SaveResultsWizard isOpen={saveOpen} onClose={() => setSaveOpen(false)} rawData={result} toolName="ADMET Prediction" toolKey="admet" smiles={ctxSmiles || smiles} moleculeId={ctxMoleculeId} fileId={ctxFileId} projectId={ctxProjectId} />
              </div>
            )}
          </div>

          <div style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <MethodCard engine="RDKit" title="ADMET Property Prediction"
              description="Absorption, Distribution, Metabolism, Excretion, and Toxicity predictions using molecular descriptors. Includes Lipinski/Veber rules, ESOL solubility, Egan absorption model, PAINS/Brenk structural alerts."
              parameters={[
                { name: 'Lipinski', desc: 'MW<=500, LogP<=5, HBD<=5, HBA<=10' },
                { name: 'ESOL', desc: 'Delaney aqueous solubility model' },
                { name: 'Egan', desc: 'GI absorption: TPSA<=131.6, LogP<=5.88' },
                { name: 'PAINS', desc: 'Pan Assay Interference alerts' },
              ]}
              reference="Delaney (2004) JCICS 44:1000-1005"
              source="RDKit Descriptors + FilterCatalog" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

const statusBarStyle: React.CSSProperties = { padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 };
const h2Style: React.CSSProperties = { color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 12, lineHeight: 1.5 };
const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 14, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' as const };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };
const resultCardStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '12px 14px' };
const resultLabelStyle: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 4 };
const resultValueStyle: React.CSSProperties = { color: '#f59e0b', fontSize: 18, fontWeight: 700, fontFamily: 'monospace' };

export default WorkbenchADMET;
